import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Logo from './Logo';
import api from '../api';

const navItems = [
  { label: 'Dashboard', icon: '📊', path: '/admin', tone: 'blue' },
  { section: 'Support' },
  { label: 'Problem Reports', icon: '🛠️', path: '/admin/reports', badge: 'reports', tone: 'coral' },
  { section: 'Students' },
  { label: 'Students', icon: '🎓', path: '/admin/students', tone: 'purple' },
  { label: 'Add Student', icon: '➕', path: '/admin/students/add', tone: 'green' },
  { section: 'Finance' },
  { label: 'Fees', icon: '💰', path: '/admin/fees', tone: 'amber' },
  { section: 'Rooms' },
  { label: 'Room Management', icon: '🏠', path: '/admin/rooms', tone: 'cyan' },
  { section: 'Staff' },
  { label: 'Staff', icon: '👥', path: '/admin/staff', tone: 'pink' },
  { label: 'Warden Details', icon: '🧑‍💼', path: '/admin/warden', tone: 'blue' },
  { label: 'Attendance', icon: '📋', path: '/admin/attendance', tone: 'green' },
  { label: 'Salaries', icon: '💵', path: '/admin/salaries', tone: 'amber' },
  { section: 'Portal' },
  { label: 'User Accounts', icon: '🔐', path: '/admin/user-accounts', tone: 'purple' },
  { section: 'Expenses' },
  { label: 'Utility Bills', icon: '⚡', path: '/admin/bills', tone: 'amber' },
  { label: 'Maintenance', icon: '🔧', path: '/admin/maintenance', tone: 'coral' },
  { section: 'Reports' },
  { label: 'Monthly Report', icon: '📈', path: '/admin/monthly-report', tone: 'cyan' },
  { section: 'Website' },
  { label: 'Front Page', icon: '⚙️', path: '/admin/customization', tone: 'pink' },
];

export default function Layout({ children, title }) {
  const { username, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [pendingReports, setPendingReports] = useState(0);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    let active = true;
    const refresh = () => api.get('/issue-reports', { params: { status: 'Pending' } }).then(response => { if (active) setPendingReports(response.data.length); }).catch(() => {});
    refresh();
    const timer = setInterval(refresh, 30000);
    return () => { active = false; clearInterval(timer); };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const toggleNavigation = () => {
    if (window.matchMedia('(max-width: 960px)').matches) {
      setMenuOpen(value => !value);
      return;
    }
    setSidebarCollapsed(value => !value);
  };

  return (
    <div className={`layout${sidebarCollapsed ? ' layout-sidebar-collapsed' : ''}`}>
      {menuOpen && <button className="sidebar-backdrop" aria-label="Close navigation" onClick={() => setMenuOpen(false)} />}
      <aside className={`sidebar${menuOpen ? ' sidebar-open' : ''}${sidebarCollapsed ? ' sidebar-collapsed' : ''}`}>
        <div className="sidebar-logo">
          <div className="sidebar-logo-mark"><Logo size={45} /></div>
          <div className="sidebar-logo-text">
            <span className="sidebar-kicker">Operations center</span>
            <h2>Royal Tower</h2>
            <p>Hostel Management</p>
          </div>
        </div>
        <nav className="sidebar-nav">
          {navItems.map((item, i) =>
            item.section ? (
              <div key={i} className="nav-section-title">{item.section}</div>
            ) : (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/admin'}
                className={({ isActive }) => `nav-item nav-tone-${item.tone || 'blue'}${isActive ? ' active' : ''}`}
                onClick={() => setMenuOpen(false)}
                title={sidebarCollapsed ? item.label : undefined}
              >
                <span className="nav-icon">{item.icon}</span>
                <span className="nav-label">{item.label}</span>
                {item.badge === 'reports' && pendingReports > 0 && <span className="nav-count">{pendingReports}</span>}
              </NavLink>
            )
          )}
        </nav>
        <NavLink className="sidebar-public-site" to="/">View public website <span>↗</span></NavLink>
      </aside>

      <div className="main-content">
        <header className="topbar">
          <div className="topbar-heading">
            <button
              className={`mobile-menu-btn sidebar-toggle-btn${sidebarCollapsed ? ' is-collapsed' : ''}`}
              type="button"
              aria-label="Toggle navigation"
              onClick={toggleNavigation}
            >
              <span />
              <span />
              <span />
            </button>
            <div className="topbar-title-block">
              <span>Royal Tower administration</span>
              <h1 className="topbar-title">{title}</h1>
            </div>
          </div>
          <div className="topbar-right">
            <span className="admin-badge"><b>A</b><span>Administrator<small>@{username}</small></span></span>
            <button className="logout-btn" onClick={handleLogout}>Logout</button>
          </div>
        </header>
        <main className="page-content">{children}</main>
      </div>
    </div>
  );
}
